import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { apArchetypesService } from '../services/apArchetypesService';
import { APArchetype } from '../types/apArchetypes';
import { Users, Clock, AlertCircle, CheckCircle, Star, FileText, Settings } from 'lucide-react';
import DataCard from '../components/common/DataCard';

const APArchetypes: React.FC = () => {
  const { accessToken, setUserAndToken } = useUser();
  const [archetypes, setArchetypes] = useState<APArchetype[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArchetypes = async () => {
      try {
        setLoading(true);
        const data = await apArchetypesService.getAPArchetypes(accessToken, setUserAndToken);
        // Sort by 'order_to_evaluate' entry directly from descriptions
        const sortedData = data.sort((a, b) => {
          const aOrder = Number(
            a.descriptions.find(d => d.is_active && d.key === 'order_to_evaluate')?.value ?? 0
          );
          const bOrder = Number(
            b.descriptions.find(d => d.is_active && d.key === 'order_to_evaluate')?.value ?? 0
          );
          return (Number.isNaN(aOrder) ? 0 : aOrder) - (Number.isNaN(bOrder) ? 0 : bOrder);
        });
        setArchetypes(sortedData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch AP archetypes');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchArchetypes();
    }
  }, [accessToken, setUserAndToken]);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AP archetypes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Archetypes</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-slate-900 mb-1 px-2">Manage AP archetype rules and criteria</h2>
        </div>
        <Link
          to="/ap-archetypes/create"
          className="bg-[#4a67b7] hover:bg-primary/90 text-white px-6 py-2 rounded-lg flex items-center shadow transition"
        >
          + Add Archetype
        </Link>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg text-slate-900">All AP Archetypes</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-500">Total Archetypes:</span>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {archetypes.length}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {archetypes.map((archetype) => (
              <DataCard
                key={archetype.id}
                data={archetype}
                dataType="apArchetype"
                showLink={true}
                linkPath={`/ap-archetypes/${archetype.id}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {archetypes.length === 0 && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Archetypes Found</h3>
            <p className="text-slate-600">No AP archetypes are currently configured.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default APArchetypes;
